import React, {useState} from 'react';
import {StorageObject} from '../../../models/StorageObject';
import {Valid} from '../../validation/Valid';
import {Accordion} from '../../Accordion/Accordion';
import {ContentWrapper, TextInputLong} from './FileMetaDataTabMain.style';
import {t} from 'i18next';
import {LabelView} from '../../LabelView/LabelView';
import {Label} from '../../../models/Label';
import {addAllLabelEntryMap, getLabelEntryMapInitialized, getLabelMapKeys} from '../../../utils/StorageObjectUtils';
import {
    FileMetaDataDialog_changeComment,
    FileMetaDataDialog_changeLabels,
    FileMetaDataDialog_changeLocales,
    FileMetaDataDialog_changeVersion,
    FileMetaDataTabLocales_changeLocales,
    FileMetaDataTabMain_changeLabels,
    LabelView_addAllLocales
} from '../../../init/events';
import cloneDeep from 'lodash/cloneDeep';
import EventEmitter from 'events';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {FileBasicData} from './FileBasicData';

export interface FileMetaDataTabMainProps {
    readonly item: StorageObject;
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly labelsInitialState: Map<string, Label>;
    readonly versionInitialState: string;
    readonly eventEmitter: EventEmitter;
}

/**
 * @name FileMetaDataTabMain
 * @description
 * The dialog to add the metadata of a newly created file or edited file - Metadata Tabs
 */
export const FileMetaDataTabMain: React.VFC<FileMetaDataTabMainProps> = (props) => {
    const [labelsInput, setLabelsInput] = useState<Map<string, Label>>(getLabelEntryMapInitialized(props.defaultLocale, props.item.name, props.item.labels));
    const [versionInput, setVersionInput] = useState(props.item.metadata.version ?? '');
    const [commentInput, setCommentInput] = useState(props.item.metadata.comment ?? '');

    const handleAddAllLocales = () => {
        let locales = addAllLabelEntryMap(props.defaultLocale, props.parentLocales, labelsInput);
        props.eventEmitter.emit(LabelView_addAllLocales, locales);
    };

    const handleResetLocales = () => {
        handleChangeLabels(cloneDeep(props.labelsInitialState));
    };

    const handleChangeLabelsState = (labels: Map<string, Label>) => {
        setLabelsInput(labels);
    };

    const handleChangeLabels = (labels: Map<string, Label>) => {
        setLabelsInput(labels);
        let locales = getLabelMapKeys(props.defaultLocale, labels);
        props.eventEmitter.emit(FileMetaDataTabLocales_changeLocales, locales);
        props.eventEmitter.emit(FileMetaDataDialog_changeLocales, locales);
        props.eventEmitter.emit(FileMetaDataDialog_changeLabels, labels);
    };

    const handleResetLabelsComponent = () => {
        handleResetLocales();
        let version = cloneDeep(props.versionInitialState);
        setVersionInput(version);
        props.eventEmitter.emit(FileMetaDataDialog_changeVersion, version);
    };

    const handleChangeVersion = (version: string) => {
        setVersionInput(version);
        props.eventEmitter.emit(FileMetaDataDialog_changeVersion, version);
    };

    const handleCommentInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        setCommentInput(eventValue);
        props.eventEmitter.emit(FileMetaDataDialog_changeComment, eventValue);
    };

    // const isValidComment = (): boolean => {
    //     if ((!!props.item) && (isValidText(props.item.id))) {
    //         return isValidText(commentInput);
    //     }
    //
    //     return true;
    // };

    let isValidCommentInput = true; // isValidComment(); //Task DXF-3159 temporary solution until template rules implementation

    props.eventEmitter.on(FileMetaDataTabMain_changeLabels, handleChangeLabelsState);

    return (
        <ContentWrapper id={generateIdentifier()}>
            <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_BASIC_INFORMATION')}>
                <FileBasicData language={props.language}
                               item={props.item}
                />
            </Accordion>
            <Accordion accordionHeadline={t('LOCALE_LABELS_CAPTION')}
                       startOpen>
                <LabelView defaultLocale={props.defaultLocale}
                           labels={labelsInput}
                           version={versionInput}
                           parentLocales={props.parentLocales}
                           eventEmitter={props.eventEmitter}
                           onLabelsChange={handleChangeLabels}
                           onVersionChange={handleChangeVersion}
                           onAddAllLabels={handleAddAllLocales}
                           onReset={handleResetLabelsComponent}/>
            </Accordion>
            <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_COMMENT')}
                       accordionSubHeadline={' ' + t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_COMMENT_INFO')}
                       startOpen>
                <Valid isValid={isValidCommentInput}
                       message={t('META_DATA_DIALOG_COMMENT_NOT_VALID_CONSTRAINT')}>
                    <TextInputLong id={generateIdentifier('comment')}
                                   label={t('UPLOAD_FILE_META_DATA_DIALOG_COMMENT_LABEL')}
                                   required
                                   value={commentInput}
                                   onChange={handleCommentInput}/>
                </Valid>
            </Accordion>
        </ContentWrapper>
    );
}